import { apiClient } from './client'
import { filenameFromHeaders } from './csvDownload'
import type { ApiResponse, PageResponse } from '@/types/api'

export type LcLedgerChannel = 'CONTENT_FEE' | 'MALL'
export type LcLedgerEntryType = 'PAYMENT' | 'REFUND'

export const LC_LEDGER_CHANNELS: LcLedgerChannel[] = ['CONTENT_FEE', 'MALL']
export const LC_LEDGER_ENTRY_TYPES: LcLedgerEntryType[] = ['PAYMENT', 'REFUND']

export const LC_LEDGER_CHANNEL_LABELS: Record<LcLedgerChannel, string> = {
  CONTENT_FEE: '콘텐츠 사용료',
  MALL: '깨봉몰',
}

export const LC_LEDGER_ENTRY_TYPE_LABELS: Record<LcLedgerEntryType, string> = {
  PAYMENT: '결제',
  REFUND: '환불',
}

export interface LcSettlementParams {
  dateFrom: string
  dateTo: string
  organizationId?: number
  channel?: LcLedgerChannel
  entryType?: LcLedgerEntryType
  includeTest?: boolean
}

export interface LcSettlementEntriesParams extends LcSettlementParams {
  page?: number
  size?: number
}

/** One dated payment or refund event. Payments are positive, refunds negative. */
export interface LcLedgerEntryRow {
  entryId: string
  entryDate: string
  entryType: LcLedgerEntryType
  channel: LcLedgerChannel
  organizationId: number
  centerName: string
  isTestCenter: boolean
  subdomain: string | null
  reference: string
  description: string
  beneficiaryName: string | null
  totalAmount: number
  textbookAmount: number
  nonTextbookAmount: number
  shippingFee: number
  provider: string | null
  cardLast4: string | null
  pgTransactionId: string | null
  refundReason: string | null
}

/** One center's period totals. Refund entries are negative, so every amount is net. */
export interface LcSettlementSummaryRow {
  organizationId: number
  centerName: string
  isTestCenter: boolean
  subdomain: string | null
  paymentCount: number
  refundCount: number
  contentFeeAmount: number
  textbookAmount: number
  nonTextbookAmount: number
  shippingAmount: number
  refundAmount: number
  totalAmount: number
}

const BASE = '/admin/lc/settlement'

export const lcSettlementApi = {
  getSummary: async (params: LcSettlementParams): Promise<LcSettlementSummaryRow[]> => {
    const res = await apiClient.get<ApiResponse<LcSettlementSummaryRow[]>>(`${BASE}/summary`, { params })
    return res.data.data ?? []
  },

  getEntries: async (params: LcSettlementEntriesParams): Promise<PageResponse<LcLedgerEntryRow>> => {
    const res = await apiClient.get<ApiResponse<PageResponse<LcLedgerEntryRow>>>(`${BASE}/entries`, { params })
    const page = res.data.data
    if (!page) {
      return { content: [], page: 0, size: params.size ?? 50, totalElements: 0, totalPages: 0, first: true, last: true }
    }
    return page
  },

  exportSummary: async (params: LcSettlementParams): Promise<{ blob: Blob; filename: string }> => {
    const res = await apiClient.get<Blob>(`${BASE}/summary/export`, { params, responseType: 'blob' })
    return { blob: res.data, filename: filenameFromHeaders(res.headers, 'lc-settlement-summary') }
  },

  exportEntries: async (params: LcSettlementParams): Promise<{ blob: Blob; filename: string }> => {
    const res = await apiClient.get<Blob>(`${BASE}/entries/export`, { params, responseType: 'blob' })
    return { blob: res.data, filename: filenameFromHeaders(res.headers, 'lc-settlement-entries') }
  },
}
